import { baseApi, type MoneyDto } from '@shared/api/baseApi';

export interface AdminSummary {
  products: { active: number; outOfStock: number };
  deposits: { pending: number; pendingTotal: MoneyDto };
  premiumProvider: {
    active: PremiumProvider;
    name: string;
    balanceUSD: string | null;
    options: { id: PremiumProvider; name: string; configured: boolean }[];
  };
  premiumPricing: { rate: string; profitPercent: string };
  hubx: { balanceUSDT: string | null };
}

export type PremiumProvider = 'HUBX' | 'ETHIO_VIRAL';

export interface AdminMoneyAnalytics {
  range: {
    from: string;
    to: string;
    timezone: 'Africa/Addis_Ababa';
    estimatedEvents: number;
  };
  rate: { usdtToEtb: string };
  cash: {
    approvedDeposits: MoneyDto;
    approvedCount: number;
  };
  sales: {
    settled: MoneyDto;
    completedCount: number;
    pending: MoneyDto;
    pendingCount: number;
    refunds: MoneyDto;
    refundCount: number;
    retainedCancellations: MoneyDto;
    retainedCancellationCount: number;
  };
  hubx: {
    spentUSDT: string;
    spentETB: MoneyDto;
    possibleSpendUSDT: string;
    liveBalanceUSDT: string | null;
  };
  profit: {
    gross: MoneyDto;
    marginPercent: string | null;
  };
  wallet: {
    actual: MoneyDto;
    expected: MoneyDto;
    mismatch: MoneyDto;
    manualAdjustments: MoneyDto;
    historicalOpeningAdjustment: MoneyDto;
    historicalOpeningCount: number;
  };
  channels: {
    retail: { orders: number; revenue: MoneyDto };
    reseller: { orders: number; revenue: MoneyDto };
  };
  products: {
    productId: string;
    name: string;
    orders: number;
    revenue: MoneyDto;
    costUSDT: string;
    costETB: MoneyDto;
    profit: MoneyDto;
  }[];
  anomalies: {
    count: number;
    awaitingDelivery: number;
    possibleHubxSpendUSDT: string;
    completedHubxWithoutId: number;
    missingLedgerEvents: number;
    walletMismatch: MoneyDto;
  };
}

export interface AdminDepositRow {
  id: string;
  amount: MoneyDto;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  amountPending: boolean;
  detectedProvider: string | null;
  detectedReferenceToken: string | null;
  detectedTransactionId: string | null;
  detectedAmount: MoneyDto | null;
  createdAt: string;
  hasReceiptImage: boolean;
  user: {
    telegramId: string;
    firstName: string | null;
    username: string | null;
    balance: MoneyDto;
  } | null;
}

export interface AdminProductRow {
  id: string;
  slug: string;
  name: string;
  stock: number;
  /** Unique delivery units currently held by the shop. */
  localStock: number;
  inventoryMode: 'PROVIDER' | 'UNLIMITED' | 'UNIQUE_ITEMS';
  deliveryMode: 'PROVIDER' | 'SHARED_MESSAGE' | 'MANUAL_QUEUE' | 'STOCK_ITEM';
  inStock: boolean;
  price: MoneyDto;
  /** Retail price before any standard or flash discount. */
  basePrice: MoneyDto;
  logoUrl: string;
  costPriceUSDT: string;
  fixedPrice: boolean;
  /** The product page / redemption steps, editable from the panel. */
  details: string | null;
  /** True when an admin has dragged this product into place. */
  placed: boolean;
  /**
   * HUBX products are synced, MANUAL ones were added in the Products tab, and
   * SMSPOOL ones are numbers pinned in the Numbers tab.
   */
  source: 'HUBX' | 'ETHIO_VIRAL' | 'MANUAL' | 'SMSPOOL';
  /** The storefront section it sits in; null while unfiled. */
  category: { id: string; slug: string; name: string } | null;
  /** False for own products and upstream products whose stock is unlimited. */
  finiteStock: boolean;
  /** False while HubX itself has delisted the product. */
  upstreamActive: boolean;
  /** HubX sale switch controlled by the operator and preserved across syncs. */
  operatorAvailable: boolean;
  /** Whether the storefront renders the wrapped Popular treatment. */
  popular: boolean;
}

export interface AdminReferralClaimRow {
  id: string;
  productId: string;
  productName: string;
  status: 'PENDING' | 'PROCESSING' | 'WAITING_STOCK' | 'FULFILLED' | 'REJECTED';
  createdAt: string;
  reviewedAt: string | null;
  fulfilledAt: string | null;
  rejectionReason: string | null;
  user: { telegramId: string; firstName: string | null; username: string | null };
}

export interface AdminReferralSnapshot {
  product: { id: string; name: string; stock: number; inStock: boolean } | null;
  claims: AdminReferralClaimRow[];
}

/** A product the operator added themselves, from the Products tab. */
export interface ManualProductRow {
  id: string;
  slug: string;
  name: string;
  price: MoneyDto;
  logoUrl: string;
  inStock: boolean;
  localStock: number;
  inventoryMode: 'UNLIMITED' | 'UNIQUE_ITEMS';
  deliveryMode: 'SHARED_MESSAGE' | 'MANUAL_QUEUE' | 'STOCK_ITEM';
  /** What every customer receives after paying; null when delivered by hand. */
  deliveryMessage: string | null;
  /** True when each order waits in the queue to be filled in per customer. */
  deliversByHand: boolean;
  /** What the buyer is asked for before paying; null when nothing is asked. */
  input: CustomerInputField | null;
  /** The product page shown before buying. */
  details: string | null;
}

/** A question the product puts to its buyer before they can pay. */
export interface CustomerInputField {
  type: 'TEXT' | 'NUMBER' | 'EMAIL';
  placeholder: string | null;
}

export interface CreateManualProductInput {
  name: string;
  priceETB: string;
  inventoryMode: 'UNLIMITED' | 'UNIQUE_ITEMS';
  deliveryMode: 'SHARED_MESSAGE' | 'MANUAL_QUEUE' | 'STOCK_ITEM';
  deliveryMessage?: string;
  initialStock?: string[];
  details?: string;
  /** Omit to ask the buyer for nothing. */
  input?: CustomerInputField;
}

export interface EditManualProductInput {
  name: string;
  priceETB: string;
  deliveryMessage: string | null;
  details: string | null;
  input: CustomerInputField | null;
}

/** A buyer, as shown beside the order they are waiting on. */
export interface DeliveryUser {
  id: string;
  telegramId: string;
  firstName: string | null;
  username: string | null;
  isBanned: boolean;
  createdAt: string;
  balance: MoneyDto;
}

/** One row of the delivery queue. */
export interface DeliveryRow {
  id: string;
  productName: string;
  pricePaid: MoneyDto;
  status: 'PENDING' | 'PAID' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | 'AWAITING_SMS';
  createdAt: string;
  deliveredItemCount: number;
  /** What the buyer answered, when the product asked. */
  customerInput: string | null;
  user: DeliveryUser | null;
}

export interface DeliveryDetail {
  order: {
    id: string;
    productId: string;
    productName: string;
    pricePaid: MoneyDto;
    status: DeliveryRow['status'];
    createdAt: string;
    deliveredItems: unknown[];
    awaitingDelivery: boolean;
    instructions: string | null;
    /** Why it was closed without a delivery; null on everything else. */
    failureReason: string | null;
    /** What the buyer answered, and what the field was called when they did. */
    customerInput: string | null;
    inputLabel: string | null;
  };
  user: DeliveryUser | null;
}

export interface CanvaOrderRow {
  id: string;
  productName: string;
  pricePaid: MoneyDto;
  status: DeliveryRow['status'];
  createdAt: string;
  email: string;
  user: DeliveryUser | null;
}

export interface CanvaOrdersResponse {
  product: { id: string; slug: string; name: string };
  orders: CanvaOrderRow[];
  pendingCount: number;
}

/** Which kind of product an order was for. */
export type AdminOrderSource = 'HUBX' | 'ETHIO_VIRAL' | 'MANUAL' | 'SMSPOOL';

/** The order history's filter; 'ALL' is what the tab opens on. */
export type AdminOrderSourceFilter = AdminOrderSource | 'ALL';

/** One row of the shop-wide order history. */
export interface AdminOrderRow {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  pricePaid: MoneyDto;
  status: DeliveryRow['status'];
  kind: 'PURCHASE' | 'GIVEAWAY';
  /** Null only when the product was deleted after the order was placed. */
  source: AdminOrderSource | null;
  /** RESELLER marks an API purchase, which no customer screen shows. */
  channel: 'RETAIL' | 'RESELLER';
  createdAt: string;
  /** null = never fulfilled, 0 = fulfilled but empty, n = delivered. */
  deliveredItemCount: number | null;
  /** The rented number, on orders that got one. */
  phoneNumber: string | null;
  user: {
    id: string;
    telegramId: string;
    firstName: string | null;
    username: string | null;
    isBanned: boolean;
  } | null;
}

/** One order from the history, with everything recorded about it. */
export interface AdminOrderDetail {
  order: {
    id: string;
    productId: string;
    productName: string;
    quantity: number;
    pricePaid: MoneyDto;
    status: DeliveryRow['status'];
    kind: 'PURCHASE' | 'GIVEAWAY';
    source: AdminOrderSource | null;
    createdAt: string;
    deliveredItems: unknown[];
    /** Upstream's reference; what a HubX support ticket is opened with. */
    hubxOrderId: string | null;
    /** The number that was rented, kept after the order settled. */
    activation: { phoneNumber: string; expiresAt: string } | null;
    failureReason: string | null;
    customerInput: string | null;
    inputLabel: string | null;
    instructions: string | null;
    /** The reseller's own order id, on API purchases only. */
    resellerExternalId: string | null;
  };
  user: DeliveryUser | null;
}

export interface AdminUserRow {
  id: string;
  telegramId: string;
  firstName: string | null;
  username: string | null;
  isBanned: boolean;
  createdAt: string;
  balance: MoneyDto;
  orderCount: number;
  totalSpent: MoneyDto;
  depositCount: number;
  totalDeposited: MoneyDto;
  referralCount: number;
  successfulReferralCount: number;
  lastSeenAt: string | null;
  isOnline: boolean;
}

export interface UserBanDto {
  id: string;
  reason: string;
  internalNote: string | null;
  expiresAt: string | null;
  createdAt: string;
  createdByTelegramId: string | null;
  revokedAt: string | null;
  revokedByTelegramId: string | null;
  revocationReason: string | null;
}

export interface AdminBanEntry extends UserBanDto {
  user: {
    id: string;
    telegramId: string;
    firstName: string | null;
    username: string | null;
  };
}

export interface AdminBanPage {
  bans: AdminBanEntry[];
  total: number;
  serverTime: string;
  stats: {
    active: number;
    permanent: number;
    temporary: number;
    history: number;
  };
}

export type AdminUserFilter =
  | 'RECENT'
  | 'BALANCE'
  | 'MOST_ORDERED'
  | 'DEPOSITED'
  | 'REFERRALS'
  | 'PRODUCT_ORDERED'
  | 'LAST_SEEN';

export interface AdminUserDetail {
  user: {
    id: string;
    telegramId: string;
    firstName: string | null;
    username: string | null;
    isBanned: boolean;
    createdAt: string;
    balance: MoneyDto;
  };
  activeBan: UserBanDto | null;
  banHistory: UserBanDto[];
  totals: {
    spent: MoneyDto;
    deposited: MoneyDto;
    orderCount: number;
    depositCount: number;
  };
  orders: {
    id: string;
    productName: string;
    pricePaid: MoneyDto;
    status: string;
    createdAt: string;
    /**
     * null when the order never reached fulfilment, 0 when it completed but
     * upstream delivered nothing — the case worth spotting. The contents
     * themselves come from getAdminOrderItems.
     */
    deliveredItemCount: number | null;
  }[];
  deposits: {
    id: string;
    amount: MoneyDto;
    amountPending: boolean;
    status: string;
    createdAt: string;
    hasReceiptImage: boolean;
  }[];
}

/** One user's satisfaction rating, as listed in the panel's Feedback tab. */
export interface AdminFeedbackRow {
  id: string;
  rating: number;
  createdAt: string;
  /** When they last changed their answer; the list is sorted on this. */
  updatedAt: string;
  user: {
    id: string;
    telegramId: string;
    firstName: string | null;
    username: string | null;
    isBanned: boolean;
  };
}

export interface AdminFeedbackPage {
  entries: AdminFeedbackRow[];
  total: number;
  /** Mean across everyone who has rated, to one decimal; null when nobody has. */
  average: number | null;
}

export interface AdminOrderItems {
  orderId: string;
  productName: string;
  status: string;
  hubxOrderId: string | null;
  deliveredItems: unknown[];
}

export interface AdminDiscountRow {
  id: string;
  kind: 'STANDARD' | 'FLASH';
  scope: 'ALL' | 'PRODUCT';
  productId: string | null;
  productName: string | null;
  type: 'PERCENT' | 'FIXED';
  value: string;
  label: string | null;
  isActive: boolean;
  featuredOnHome: boolean;
  /** Active *and* inside its date window. */
  isLive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
}

export type AdminGiveawayReaction = 'LIKE' | 'LOVE' | 'CARE' | 'WOW' | 'CELEBRATE' | 'FIRE';

export interface AdminGiveawayRow {
  id: string;
  productId: string;
  productName: string;
  participantLimit: number;
  participantCount: number;
  winnerCount: number;
  deliveryMessages: (string | null)[];
  reactionCounts: Record<AdminGiveawayReaction, number>;
  status: 'LIVE' | 'CLOSED' | 'DRAWN' | 'DELIVERED' | 'CANCELLED';
  featuredOnHome: boolean;
  startsAt: string;
  endsAt: string;
  winnerUserId: string | null;
  winnerOrderId: string | null;
  winners: {
    id: string;
    userId: string;
    orderId: string;
    position: number;
    deliveredAt: string | null;
    firstName: string | null;
    username: string | null;
  }[];
  drawnAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  winner: {
    telegramId: string;
    username: string | null;
    firstName: string | null;
  } | null;
}

export type AdminFlashDrawStatus =
  | 'UPCOMING'
  | 'LIVE'
  | 'FULL'
  | 'DRAWING'
  | 'COMPLETED'
  | 'CANCELLED';

export interface AdminFlashDrawRow {
  id: string;
  productId: string;
  productName: string;
  productSlug: string;
  entryPrice: MoneyDto;
  participantLimit: number;
  participantCount: number;
  status: AdminFlashDrawStatus;
  visibleOnHome: boolean;
  startsAt: string;
  endsAt: string;
  drawingDelaySeconds: number;
  deliveryMessage: string;
  fullAt: string | null;
  drawAt: string | null;
  drawnAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  announcedAt: string | null;
  createdAt: string;
  winner: {
    userId: string;
    telegramId: string;
    firstName: string | null;
  } | null;
  audit: { winnerRandomIndex: number | null; participantPoolHash: string | null };
  participants: {
    userId: string;
    telegramId: string;
    firstName: string | null;
    username: string | null;
    entryPrice: MoneyDto;
    paidAt: string;
    refundedAt: string | null;
    resultNotifiedAt: string | null;
  }[];
}

export interface CreateDiscountInput {
  kind?: 'STANDARD' | 'FLASH';
  scope: 'ALL' | 'PRODUCT';
  productId?: string;
  type: 'PERCENT' | 'FIXED';
  value: string;
  label?: string;
  startsAt?: string;
  endsAt?: string;
  durationSeconds?: number;
  featuredOnHome?: boolean;
}

export type FlashDealBroadcastTarget = BroadcastTarget;

export interface FlashDealBroadcastResult {
  target: FlashDealBroadcastTarget;
  botQueued: boolean;
  channelMessageId: number | null;
}

/** Where a broadcast goes. Shared by flash deals and price updates. */
export type BroadcastTarget = 'BOT' | 'CHANNEL' | 'BOTH';

export interface PriceUpdateBroadcastResult {
  productId: string;
  previousPriceLabel: string;
  priceLabel: string;
  target: BroadcastTarget;
  botQueued: boolean;
  channelMessageId: number | null;
}

export interface PriceUpdateLine {
  productName: string;
  previousPriceLabel: string;
  priceLabel: string;
}

export interface BatchPriceUpdateResult {
  lines: PriceUpdateLine[];
  target: BroadcastTarget;
  botQueued: boolean;
  channelMessageId: number | null;
}

export interface AdminRow {
  telegramId: string;
  firstName: string | null;
  username: string | null;
  note: string | null;
  addedBy: string | null;
  createdAt: string;
}

export interface MaintenanceState {
  enabled: boolean;
  message: string | null;
  imageUrl: string | null;
}

export interface CelebrationSettings {
  recurringEnabled: boolean;
  newUsersEnabled: boolean;
  intervalDays: 1 | 2;
}

export interface BackupEntry {
  name: string;
  kind: 'automatic' | 'manual' | 'pre-restore';
  createdAt: string;
  sizeBytes: number;
  warnings: string[];
}

export interface BackupStatus {
  intervalHours: number | null;
  retentionCount: number;
  nextRunAt: string | null;
  running: boolean;
  restoring: boolean;
  lastError: string | null;
  canRestore: boolean;
  backups: BackupEntry[];
}

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminSummary: builder.query<AdminSummary, void>({
      query: () => '/admin/summary',
      providesTags: ['AdminSummary'],
    }),

    getMoneyAnalytics: builder.query<AdminMoneyAnalytics, { from: string; to: string }>({
      query: ({ from, to }) =>
        `/admin/analytics?${new URLSearchParams({ from, to }).toString()}`,
      providesTags: ['AdminAnalytics'],
    }),

    getAdminDeposits: builder.query<AdminDepositRow[], 'PENDING' | 'APPROVED' | 'REJECTED'>({
      query: (status) => `/admin/deposits?status=${status}`,
      transformResponse: (response: { deposits: AdminDepositRow[] }) => response.deposits,
      providesTags: ['AdminDeposit'],
    }),

    approveDeposit: builder.mutation<
      unknown,
      { id: string; amountETB: string } | { id: string; useDetectedAmount: true }
    >({
      query: (input) => ({
        url: `/admin/deposits/${input.id}/approve`,
        method: 'POST',
        body: 'useDetectedAmount' in input
          ? { useDetectedAmount: true }
          : { amountETB: input.amountETB },
      }),
      invalidatesTags: ['AdminDeposit', 'AdminSummary', 'AdminAnalytics', 'Deposit', 'User'],
    }),

    rejectDeposit: builder.mutation<unknown, { id: string; note?: string }>({
      query: ({ id, note }) => ({
        url: `/admin/deposits/${id}/reject`,
        method: 'POST',
        body: note ? { note } : {},
      }),
      invalidatesTags: ['AdminDeposit', 'AdminSummary', 'Deposit'],
    }),

    getAdminReferrals: builder.query<AdminReferralSnapshot, void>({
      query: () => '/admin/referrals',
      providesTags: ['AdminReferral'],
    }),

    setReferralProduct: builder.mutation<{ productId: string | null }, string | null>({
      query: (productId) => ({
        url: '/admin/referrals/product',
        method: 'PUT',
        body: { productId },
      }),
      invalidatesTags: ['AdminReferral', 'Referral'],
    }),

    approveReferralClaim: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/admin/referrals/${id}/approve`, method: 'POST' }),
      invalidatesTags: ['AdminReferral', 'Referral'],
    }),

    rejectReferralClaim: builder.mutation<unknown, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/admin/referrals/${id}/reject`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      invalidatesTags: ['AdminReferral', 'Referral'],
    }),

    getAdminProducts: builder.query<AdminProductRow[], void>({
      query: () => '/admin/products',
      transformResponse: (response: { products: AdminProductRow[] }) => response.products,
      providesTags: ['AdminProduct'],
    }),

    addProductStock: builder.mutation<
      {
        stock: {
          productId: string;
          productName: string;
          added: number;
          available: number;
          totalAvailable: number;
          createdAt: string;
          broadcast:
            | { published: true; messageId: number; broadcastId: string | null }
            | { published: false };
        };
      },
      { productId: string; deliveryTexts: string[] }
    >({
      query: (body) => ({ url: '/admin/product-stock', method: 'POST', body }),
      invalidatesTags: ['AdminProduct', 'ManualProduct', 'Product', 'AdminSummary'],
    }),

    setProductPrice: builder.mutation<unknown, { slug: string; priceETB: string | null }>({
      query: ({ slug, priceETB }) => ({
        url: `/admin/products/${slug}/price`,
        method: 'POST',
        body: { priceETB },
      }),
      invalidatesTags: ['AdminProduct', 'ManualProduct', 'Product'],
    }),

    announceNewArrival: builder.mutation<
      { productId: string; priceLabel: string; queued: true; channelMessageId: number },
      string
    >({
      query: (slug) => ({
        url: `/admin/products/${slug}/new-arrival`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminProduct', 'Product'],
    }),

    // Announces the price already set on the product against the one the admin
    // types, so the price itself is changed through setProductPrice first.
    broadcastPriceUpdate: builder.mutation<
      PriceUpdateBroadcastResult,
      { slug: string; previousPriceETB: string; target: BroadcastTarget }
    >({
      query: ({ slug, previousPriceETB, target }) => ({
        url: `/admin/products/${slug}/price-update`,
        method: 'POST',
        body: { previousPriceETB, target },
      }),
    }),

    // One combined post for several products; the server refuses the whole
    // selection rather than sending a partly-correct list.
    broadcastBatchPriceUpdate: builder.mutation<
      BatchPriceUpdateResult,
      { items: { slug: string; previousPriceETB: string }[]; target: BroadcastTarget }
    >({
      query: (body) => ({
        url: '/admin/products/price-updates',
        method: 'POST',
        body,
      }),
    }),

    publishLowStock: builder.mutation<{ messageId: number; stock: number }, string>({
      query: (slug) => ({
        url: `/admin/products/${slug}/channel-stock`,
        method: 'POST',
      }),
    }),

    setProductAvailability: builder.mutation<
      { operatorAvailable: boolean },
      { slug: string; available: boolean }
    >({
      query: ({ slug, available }) => ({
        url: `/admin/products/${slug}/availability`,
        method: 'POST',
        body: { available },
      }),
      invalidatesTags: ['AdminProduct', 'Product', 'AdminSummary'],
    }),

    setProductPopular: builder.mutation<
      { popular: boolean },
      { slug: string; popular: boolean }
    >({
      query: ({ slug, popular }) => ({
        url: `/admin/products/${slug}/popular`,
        method: 'POST',
        body: { popular },
      }),
      invalidatesTags: ['AdminProduct', 'Product'],
    }),

    getManualProducts: builder.query<ManualProductRow[], void>({
      query: () => '/admin/manual-products',
      transformResponse: (response: { products: ManualProductRow[] }) => response.products,
      providesTags: ['ManualProduct'],
    }),

    createManualProduct: builder.mutation<{ product: ManualProductRow }, CreateManualProductInput>({
      query: (body) => ({ url: '/admin/manual-products', method: 'POST', body }),
      // It is on sale immediately, so the storefront and the panel are stale.
      invalidatesTags: ['ManualProduct', 'AdminProduct', 'Product', 'AdminSummary'],
    }),

    editManualProduct: builder.mutation<
      { product: ManualProductRow },
      { slug: string; changes: EditManualProductInput }
    >({
      query: ({ slug, changes }) => ({
        url: `/admin/manual-products/${slug}`,
        method: 'PUT',
        body: changes,
      }),
      invalidatesTags: ['ManualProduct', 'AdminProduct', 'Product', 'Order'],
    }),

    setManualProductDelivery: builder.mutation<
      unknown,
      { slug: string; deliveryMessage: string | null }
    >({
      query: ({ slug, deliveryMessage }) => ({
        url: `/admin/manual-products/${slug}/delivery`,
        method: 'POST',
        body: { deliveryMessage },
      }),
      invalidatesTags: ['ManualProduct'],
    }),

    getDeliveries: builder.query<
      { orders: DeliveryRow[]; awaitingCount: number },
      'awaiting' | 'delivered'
    >({
      query: (status) => `/admin/deliveries?status=${status}`,
      providesTags: ['Delivery'],
    }),

    getPendingDeliveryCount: builder.query<{ count: number }, void>({
      query: () => '/admin/deliveries/count',
      providesTags: ['Delivery'],
    }),

    getCanvaOrders: builder.query<CanvaOrdersResponse, 'pending' | 'history'>({
      query: (status) => `/admin/canva-orders?status=${status}`,
      providesTags: ['CanvaOrder'],
    }),

    deliverCanvaOrder: builder.mutation<
      { order: { id: string; status: DeliveryRow['status'] }; message: string },
      string
    >({
      query: (orderId) => ({
        url: `/admin/canva-orders/${orderId}/deliver`,
        method: 'POST',
      }),
      invalidatesTags: ['CanvaOrder', 'Delivery', 'Order', 'AdminOrder', 'AdminAnalytics'],
    }),

    getDelivery: builder.query<DeliveryDetail, string>({
      query: (orderId) => `/admin/deliveries/${orderId}`,
      providesTags: ['Delivery'],
    }),

    deliverOrder: builder.mutation<unknown, { orderId: string; message: string }>({
      query: ({ orderId, message }) => ({
        url: `/admin/deliveries/${orderId}/deliver`,
        method: 'POST',
        body: { message },
      }),
      // The customer's own order list flips to delivered at the same moment.
      invalidatesTags: ['Delivery', 'Order', 'AdminOrder', 'AdminAnalytics'],
    }),

    cancelOrder: builder.mutation<
      { order: { id: string; status: DeliveryRow['status'] }; refunded: boolean },
      { orderId: string; refund: boolean; reason: string | null }
    >({
      query: ({ orderId, refund, reason }) => ({
        url: `/admin/deliveries/${orderId}/cancel`,
        method: 'POST',
        body: { refund, reason },
      }),
      // A refund moves the customer's balance, so their wallet is stale too —
      // and the panel's totals count only orders that completed.
      invalidatesTags: [
        'Delivery',
        'Order',
        'AdminOrder',
        'User',
        'AdminUser',
        'AdminSummary',
        'AdminAnalytics',
      ],
    }),

    setManualProductInput: builder.mutation<
      unknown,
      { slug: string; input: CustomerInputField | null }
    >({
      query: ({ slug, input }) => ({
        url: `/admin/manual-products/${slug}/input`,
        method: 'POST',
        body: { input },
      }),
      // The storefront renders the field, so its cache is stale too.
      invalidatesTags: ['ManualProduct', 'Product'],
    }),

    deleteManualProduct: builder.mutation<
      { removed: string; outcome: 'deleted' | 'delisted' },
      string
    >({
      query: (slug) => ({ url: `/admin/manual-products/${slug}`, method: 'DELETE' }),
      invalidatesTags: ['ManualProduct', 'AdminProduct', 'Product', 'AdminSummary'],
    }),

    reorderProducts: builder.mutation<{ placed: number }, string[]>({
      query: (productIds) => ({
        url: '/admin/products/order',
        method: 'POST',
        body: { productIds },
      }),
      invalidatesTags: ['AdminProduct', 'Product'],
    }),

    resetProductOrder: builder.mutation<{ cleared: number }, void>({
      query: () => ({ url: '/admin/products/order', method: 'DELETE' }),
      invalidatesTags: ['AdminProduct', 'Product'],
    }),

    setProductInstructions: builder.mutation<unknown, { slug: string; instructions: string | null }>({
      query: ({ slug, instructions }) => ({
        url: `/admin/products/${slug}/instructions`,
        method: 'POST',
        body: { instructions },
      }),
      // Customers read these after purchase, so their order list is stale too.
      invalidatesTags: ['AdminProduct', 'ManualProduct', 'Product', 'Order'],
    }),

    setProductLogo: builder.mutation<
      { logoUrl: string; transparent: boolean },
      { slug: string; imageBase64: string; background?: 'auto' | 'transparent' | 'white' }
    >({
      query: ({ slug, imageBase64, background = 'auto' }) => ({
        url: `/admin/products/${slug}/logo`,
        method: 'POST',
        body: { imageBase64, background },
      }),
      invalidatesTags: ['AdminProduct', 'ManualProduct', 'Product'],
    }),

    syncProducts: builder.mutation<
      {
        synced: number;
        deactivated: number;
        rate: string;
        profitPercent: string;
        provider: PremiumProvider;
      },
      void
    >({
      query: () => ({ url: '/admin/sync', method: 'POST' }),
      invalidatesTags: ['AdminProduct', 'Product', 'AdminSummary'],
    }),

    setPremiumProvider: builder.mutation<
      {
        synced: number;
        deactivated: number;
        rate: string;
        provider: PremiumProvider;
        name: string;
      },
      PremiumProvider
    >({
      query: (provider) => ({ url: '/admin/provider', method: 'POST', body: { provider } }),
      invalidatesTags: ['AdminProduct', 'Product', 'AdminSummary', 'AdminAnalytics'],
    }),

    setPremiumPricing: builder.mutation<
      {
        settings: { rate: string; profitPercent: string };
        repriced: number;
        provider: PremiumProvider;
      },
      { rate: string; profitPercent: string }
    >({
      query: (body) => ({ url: '/admin/pricing', method: 'POST', body }),
      invalidatesTags: ['AdminProduct', 'Product', 'AdminSummary', 'AdminAnalytics'],
    }),

    adjustBalance: builder.mutation<unknown, { telegramId: string; deltaETB: string }>({
      query: ({ telegramId, deltaETB }) => ({
        url: `/admin/users/${telegramId}/balance`,
        method: 'POST',
        body: { deltaETB },
      }),
      invalidatesTags: ['User', 'AdminUser', 'AdminAnalytics'],
    }),

    getAdminUsers: builder.query<
      { users: AdminUserRow[]; total: number; serverTime: string },
      { query?: string; offset?: number; sort?: AdminUserFilter; productId?: string }
    >({
      query: ({ query, offset = 0, sort = 'RECENT', productId }) => {
        const params = new URLSearchParams({ offset: String(offset), limit: '30' });
        if (query) params.set('query', query);
        params.set('sort', sort);
        if (productId) params.set('productId', productId);
        return `/admin/users?${params.toString()}`;
      },
      providesTags: ['AdminUser'],
    }),

    getAdminFeedback: builder.query<AdminFeedbackPage, { offset?: number }>({
      query: ({ offset = 0 }) =>
        `/admin/feedback?${new URLSearchParams({ offset: String(offset), limit: '30' })}`,
      providesTags: ['AdminFeedback'],
    }),

    getAdminUser: builder.query<AdminUserDetail, string>({
      query: (telegramId) => `/admin/users/${telegramId}`,
      providesTags: ['AdminUser'],
    }),

    getAdminOrders: builder.query<
      { orders: AdminOrderRow[]; total: number },
      { source?: AdminOrderSourceFilter; order?: 'newest' | 'oldest'; offset?: number }
    >({
      query: ({ source = 'ALL', order = 'newest', offset = 0 }) =>
        `/admin/orders?${new URLSearchParams({ source, order, offset: String(offset), limit: '30' })}`,
      providesTags: ['AdminOrder'],
    }),

    // Not cached under a tag, for the same reason as getAdminOrderItems: the
    // response carries what a customer was delivered, and every read of that
    // is logged server-side. It should follow an admin opening the order, not
    // a background refetch.
    getAdminOrder: builder.query<AdminOrderDetail, string>({
      query: (orderId) => `/admin/orders/${orderId}`,
    }),

    // Not cached under a tag: each read is logged server-side, so it should
    // happen when an admin actually asks, not on a background refetch.
    getAdminOrderItems: builder.query<AdminOrderItems, string>({
      query: (orderId) => `/admin/orders/${orderId}/items`,
    }),

    getAdminBans: builder.query<
      AdminBanPage,
      { status?: 'ACTIVE' | 'HISTORY'; query?: string; offset?: number }
    >({
      query: ({ status = 'ACTIVE', query, offset = 0 }) => {
        const params = new URLSearchParams({ status, offset: String(offset), limit: '30' });
        if (query) params.set('query', query);
        return `/admin/bans?${params.toString()}`;
      },
      providesTags: ['Ban'],
    }),

    setUserBanned: builder.mutation<
      { isBanned: boolean; activeBan: UserBanDto | null },
      {
        telegramId: string;
        banned: boolean;
        reason?: string;
        internalNote?: string;
        durationMinutes?: number | null;
        revocationReason?: string;
      }
    >({
      query: ({ telegramId, ...body }) => ({
        url: `/admin/users/${telegramId}/ban`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AdminUser', 'Ban'],
    }),

    getDiscounts: builder.query<AdminDiscountRow[], void>({
      query: () => '/admin/discounts',
      transformResponse: (response: { discounts: AdminDiscountRow[] }) => response.discounts,
      providesTags: ['Discount'],
    }),

    getAdminGiveaways: builder.query<AdminGiveawayRow[], void>({
      query: () => '/admin/giveaways',
      transformResponse: (response: { giveaways: AdminGiveawayRow[] }) => response.giveaways,
      providesTags: ['AdminGiveaway'],
    }),

    getAdminFlashDraws: builder.query<AdminFlashDrawRow[], void>({
      query: () => '/admin/flash-draws',
      transformResponse: (response: { draws: AdminFlashDrawRow[] }) => response.draws,
      providesTags: ['AdminFlashDraw'],
    }),

    createFlashDraw: builder.mutation<
      { id: string; status: AdminFlashDrawStatus },
      {
        productId: string;
        entryPriceETB: string;
        participantLimit: number;
        startsAt: string;
        endsAt: string;
        drawingDelaySeconds: number;
        deliveryMessage: string;
      }
    >({
      query: (body) => ({ url: '/admin/flash-draws', method: 'POST', body }),
      invalidatesTags: ['AdminFlashDraw', 'FlashDraw', 'Deposit'],
    }),

    broadcastFlashDraw: builder.mutation<{ channelMessageId: number }, string>({
      query: (id) => ({ url: `/admin/flash-draws/${id}/broadcast`, method: 'POST' }),
      invalidatesTags: ['AdminFlashDraw'],
    }),

    cancelFlashDraw: builder.mutation<unknown, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/admin/flash-draws/${id}/cancel`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['AdminFlashDraw', 'FlashDraw', 'User', 'AdminAnalytics', 'Deposit'],
    }),

    setFlashDrawVisibility: builder.mutation<unknown, { id: string; visibleOnHome: boolean }>({
      query: ({ id, visibleOnHome }) => ({
        url: `/admin/flash-draws/${id}/visibility`,
        method: 'POST',
        body: { visibleOnHome },
      }),
      invalidatesTags: ['AdminFlashDraw', 'FlashDraw'],
    }),

    updateFlashDrawDelivery: builder.mutation<unknown, { id: string; deliveryMessage: string }>({
      query: ({ id, deliveryMessage }) => ({
        url: `/admin/flash-draws/${id}/delivery`,
        method: 'PATCH',
        body: { deliveryMessage },
      }),
      invalidatesTags: ['AdminFlashDraw'],
    }),

    updateFlashDrawEntryClose: builder.mutation<{ id: string; endsAt: string }, { id: string; endsAt: string }>({
      query: ({ id, endsAt }) => ({
        url: `/admin/flash-draws/${id}/entry-close`,
        method: 'PATCH',
        body: { endsAt },
      }),
      invalidatesTags: ['AdminFlashDraw', 'FlashDraw', 'Deposit'],
    }),

    deleteFlashDraw: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/admin/flash-draws/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminFlashDraw', 'FlashDraw'],
    }),

    createGiveaway: builder.mutation<
      { id: string },
      {
        productId: string;
        participantLimit: number;
        winnerCount: number;
        deliveryMessages: (string | null)[];
        durationSeconds: number;
        featuredOnHome: boolean;
      }
    >({
      query: (body) => ({ url: '/admin/giveaways', method: 'POST', body }),
      invalidatesTags: ['AdminGiveaway', 'Giveaway'],
    }),

    drawGiveaway: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/admin/giveaways/${id}/draw`, method: 'POST' }),
      invalidatesTags: ['AdminGiveaway', 'Giveaway', 'Order'],
    }),

    broadcastGiveaway: builder.mutation<{ channelMessageId: number }, string>({
      query: (id) => ({ url: `/admin/giveaways/${id}/broadcast`, method: 'POST' }),
    }),

    updateGiveaway: builder.mutation<
      unknown,
      {
        id: string;
        participantLimit?: number;
        winnerCount?: number;
        durationSeconds?: number;
        deliveryMessages?: (string | null)[];
      }
    >({
      query: ({ id, ...body }) => ({ url: `/admin/giveaways/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['AdminGiveaway', 'Giveaway'],
    }),

    setGiveawayFeatured: builder.mutation<unknown, { id: string; featuredOnHome: boolean }>({
      query: ({ id, featuredOnHome }) => ({
        url: `/admin/giveaways/${id}/featured`,
        method: 'POST',
        body: { featuredOnHome },
      }),
      invalidatesTags: ['AdminGiveaway', 'Giveaway'],
    }),

    cancelGiveaway: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/admin/giveaways/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['AdminGiveaway', 'Giveaway'],
    }),

    deliverGiveaway: builder.mutation<unknown, { id: string; winnerId: string; message: string }>({
      query: ({ id, winnerId, message }) => ({
        url: `/admin/giveaways/${id}/winners/${winnerId}/deliver`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: ['AdminGiveaway', 'Giveaway', 'Order'],
    }),

    createDiscount: builder.mutation<{ id: string }, CreateDiscountInput>({
      query: (body) => ({ url: '/admin/discounts', method: 'POST', body }),
      // Prices change everywhere, so the storefront caches go too.
      invalidatesTags: ['Discount', 'Product', 'AdminProduct'],
    }),

    setDiscountActive: builder.mutation<unknown, { id: string; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/admin/discounts/${id}/active`,
        method: 'POST',
        body: { isActive },
      }),
      invalidatesTags: ['Discount', 'Product', 'AdminProduct'],
    }),

    updateFlashDealTime: builder.mutation<
      { startsAt: string; endsAt: string; isActive: boolean },
      { id: string; durationSeconds: number }
    >({
      query: ({ id, durationSeconds }) => ({
        url: `/admin/discounts/${id}/time`,
        method: 'POST',
        body: { durationSeconds },
      }),
      invalidatesTags: ['Discount', 'Product', 'AdminProduct'],
    }),

    setFlashDealFeatured: builder.mutation<
      { featuredOnHome: boolean },
      { id: string; featuredOnHome: boolean }
    >({
      query: ({ id, featuredOnHome }) => ({
        url: `/admin/discounts/${id}/featured`,
        method: 'POST',
        body: { featuredOnHome },
      }),
      invalidatesTags: ['Discount', 'Product', 'AdminProduct'],
    }),

    deleteDiscount: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/admin/discounts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Discount', 'Product', 'AdminProduct'],
    }),

    broadcastFlashDeal: builder.mutation<
      FlashDealBroadcastResult,
      { id: string; target: FlashDealBroadcastTarget }
    >({
      query: ({ id, target }) => ({
        url: `/admin/discounts/${id}/broadcast`,
        method: 'POST',
        body: { target },
      }),
    }),

    getAdmins: builder.query<AdminRow[], void>({
      query: () => '/admin/admins',
      transformResponse: (response: { admins: AdminRow[] }) => response.admins,
      providesTags: ['Admin'],
    }),

    grantAdmin: builder.mutation<unknown, { telegramId: string; note?: string }>({
      query: (body) => ({ url: '/admin/admins', method: 'POST', body }),
      invalidatesTags: ['Admin'],
    }),

    revokeAdmin: builder.mutation<unknown, string>({
      query: (telegramId) => ({ url: `/admin/admins/${telegramId}`, method: 'DELETE' }),
      invalidatesTags: ['Admin'],
    }),

    getAdminCelebration: builder.query<CelebrationSettings, void>({
      query: () => '/admin/celebration',
      providesTags: ['Celebration'],
    }),

    setAdminCelebration: builder.mutation<
      CelebrationSettings,
      Partial<CelebrationSettings>
    >({
      query: (body) => ({ url: '/admin/celebration', method: 'POST', body }),
      invalidatesTags: ['Celebration'],
    }),

    getAdminMaintenance: builder.query<MaintenanceState, void>({
      query: () => '/admin/maintenance',
      providesTags: ['Maintenance'],
    }),

    setAdminMaintenance: builder.mutation<
      MaintenanceState,
      { enabled?: boolean; message?: string | null }
    >({
      query: (body) => ({ url: '/admin/maintenance', method: 'POST', body }),
      // Also refreshes the public gate the whole app reads on load.
      invalidatesTags: ['Maintenance'],
    }),

    setAdminMaintenanceImage: builder.mutation<MaintenanceState, { imageBase64: string | null }>({
      query: ({ imageBase64 }) => ({
        url: '/admin/maintenance/image',
        method: 'POST',
        body: { imageBase64 },
      }),
      invalidatesTags: ['Maintenance'],
    }),

    getBackups: builder.query<BackupStatus, void>({
      query: () => '/admin/backups',
      providesTags: ['Backup'],
    }),

    setBackupInterval: builder.mutation<BackupStatus, number | null>({
      query: (intervalHours) => ({
        url: '/admin/backups/schedule',
        method: 'POST',
        body: { intervalHours },
      }),
      invalidatesTags: ['Backup'],
    }),

    createBackup: builder.mutation<BackupEntry, void>({
      query: () => ({ url: '/admin/backups', method: 'POST' }),
      invalidatesTags: ['Backup'],
    }),

    downloadBackup: builder.mutation<Blob, string>({
      query: (name) => ({
        url: `/admin/backups/${encodeURIComponent(name)}/download`,
        method: 'GET',
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    restoreStoredBackup: builder.mutation<{ restoredAt: string }, string>({
      query: (name) => ({
        url: `/admin/backups/${encodeURIComponent(name)}/restore`,
        method: 'POST',
        body: { confirmation: 'RESTORE' },
      }),
      invalidatesTags: ['Backup', 'User', 'Product', 'Order', 'Admin'],
    }),

    importBackup: builder.mutation<{ restoredAt: string }, File>({
      query: (file) => ({
        url: '/admin/backups/import',
        method: 'POST',
        headers: { 'X-Backup-Confirmation': 'RESTORE' },
        // fetchBaseQuery strips Content-Type from non-JSON bodies. Giving the
        // Blob the portable backup type lets fetch set the intended value.
        body: file.slice(0, file.size, 'application/vnd.yeneshop.backup'),
        timeout: 10 * 60_000,
      }),
      invalidatesTags: ['Backup', 'User', 'Product', 'Order', 'Admin'],
    }),
  }),
});

export const {
  useGetAdminFlashDrawsQuery,
  useCreateFlashDrawMutation,
  useBroadcastFlashDrawMutation,
  useCancelFlashDrawMutation,
  useSetFlashDrawVisibilityMutation,
  useUpdateFlashDrawDeliveryMutation,
  useUpdateFlashDrawEntryCloseMutation,
  useDeleteFlashDrawMutation,
  useGetAdminGiveawaysQuery,
  useCreateGiveawayMutation,
  useDrawGiveawayMutation,
  useBroadcastGiveawayMutation,
  useUpdateGiveawayMutation,
  useSetGiveawayFeaturedMutation,
  useCancelGiveawayMutation,
  useDeliverGiveawayMutation,
  useGetDiscountsQuery,
  useCreateDiscountMutation,
  useSetDiscountActiveMutation,
  useUpdateFlashDealTimeMutation,
  useSetFlashDealFeaturedMutation,
  useDeleteDiscountMutation,
  useBroadcastFlashDealMutation,
  useGetAdminUsersQuery,
  useGetAdminBansQuery,
  useGetAdminFeedbackQuery,
  useGetAdminUserQuery,
  useGetAdminOrdersQuery,
  useGetAdminOrderQuery,
  useLazyGetAdminOrderItemsQuery,
  useSetUserBannedMutation,
  useGetAdminSummaryQuery,
  useGetMoneyAnalyticsQuery,
  useGetAdminDepositsQuery,
  useApproveDepositMutation,
  useRejectDepositMutation,
  useGetAdminReferralsQuery,
  useSetReferralProductMutation,
  useApproveReferralClaimMutation,
  useRejectReferralClaimMutation,
  useGetAdminProductsQuery,
  useAddProductStockMutation,
  useSetProductPriceMutation,
  useAnnounceNewArrivalMutation,
  useBroadcastPriceUpdateMutation,
  useBroadcastBatchPriceUpdateMutation,
  usePublishLowStockMutation,
  useSetProductAvailabilityMutation,
  useSetProductPopularMutation,
  useSetProductInstructionsMutation,
  useSetProductLogoMutation,
  useReorderProductsMutation,
  useResetProductOrderMutation,
  useSyncProductsMutation,
  useSetPremiumProviderMutation,
  useSetPremiumPricingMutation,
  useGetManualProductsQuery,
  useCreateManualProductMutation,
  useEditManualProductMutation,
  useSetManualProductDeliveryMutation,
  useSetManualProductInputMutation,
  useDeleteManualProductMutation,
  useGetDeliveriesQuery,
  useGetCanvaOrdersQuery,
  useDeliverCanvaOrderMutation,
  useGetPendingDeliveryCountQuery,
  useGetDeliveryQuery,
  useDeliverOrderMutation,
  useCancelOrderMutation,
  useAdjustBalanceMutation,
  useGetAdminsQuery,
  useGrantAdminMutation,
  useRevokeAdminMutation,
  useGetAdminCelebrationQuery,
  useSetAdminCelebrationMutation,
  useGetAdminMaintenanceQuery,
  useSetAdminMaintenanceMutation,
  useSetAdminMaintenanceImageMutation,
  useGetBackupsQuery,
  useSetBackupIntervalMutation,
  useCreateBackupMutation,
  useDownloadBackupMutation,
  useRestoreStoredBackupMutation,
  useImportBackupMutation,
} = adminApi;
